import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { LogLines } from "./LogLines";
import { classifyLine, type LogLevel } from "../lib/logLevel";
import { Button, Input, cx } from "./ui";

/**
 * A log console, in the shape every platform of this kind converges on: one
 * toolbar, one full-bleed pane, and the newest line at the bottom.
 *
 * Shared by the deployment build log and the application runtime log, which
 * had begun to diverge — one of them gaining a filter the other did not is how
 * two views of the same thing end up feeling like two products.
 *
 * Filtering is client-side here. That is right for a build log, which is one
 * bounded blob already downloaded, and right for the application log, whose
 * upstream endpoint takes no search parameter at all. The database viewer
 * filters on the server instead, because those endpoints do take one and a
 * busy database should not ship its whole history to be grepped.
 */
/* The three filters offered, in order, and what each is called. */
const LEVELS = ["all", "error", "warn"] as const;
const LEVEL_KEYS = {
  all: "logs.all",
  error: "logs.errors",
  warn: "logs.warnings",
} as const;

export function LogViewer({
  logs,
  emptyMessage,
  downloadName,
  streaming = false,
  toolbarExtra,
}: {
  logs: string;
  emptyMessage: string;
  /** Filename for the download, without an extension. */
  downloadName: string;
  streaming?: boolean;
  toolbarExtra?: ReactNode;
}) {
  const { t } = useTranslation("common");
  const scrollRef = useRef<HTMLDivElement>(null);
  const [follow, setFollow] = useState(true);
  const [filter, setFilter] = useState("");
  const [level, setLevel] = useState<"all" | Exclude<LogLevel, null>>("all");
  const [wrap, setWrap] = useState(false);

  const all = useMemo(() => logs.trimEnd().split("\n"), [logs]);

  const shown = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    return all.filter((line) => {
      if (needle && !line.toLowerCase().includes(needle)) return false;
      if (level !== "all" && classifyLine(line) !== level) return false;
      return true;
    });
  }, [all, filter, level]);

  useEffect(() => {
    const element = scrollRef.current;
    if (follow && element) element.scrollTop = element.scrollHeight;
  }, [shown, follow]);

  const empty = all.length === 1 && all[0] === "";
  const narrowed = filter.trim().length > 0 || level !== "all";

  function download() {
    // Worth keeping when a support thread outlives the page. The whole log,
    // not the filtered view — the filter is for reading, not for exporting.
    const blob = new Blob([all.join("\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${downloadName}.log`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="border border-line">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-3 border-b border-line bg-surface px-3 py-2.5">
        <Input
          aria-label={t("logs.filter")}
          placeholder={t("logs.filterPlaceholder")}
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          className="h-8 w-full py-1 font-mono text-xs sm:w-52"
        />

        {/*
          Chips rather than a select: three options, and which one is active is
          worth being able to see without opening anything.
        */}
        <div className="flex items-center gap-px" role="group" aria-label={t("logs.byLevel")}>
          {LEVELS.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setLevel(option)}
              aria-pressed={level === option}
              className={cx(
                "h-8 px-2.5 text-xs font-medium transition-colors duration-200 ease-smooth",
                level === option ? "bg-raised text-fg" : "text-fg-subtle hover:text-fg",
              )}
            >
              {t(LEVEL_KEYS[option])}
            </button>
          ))}
        </div>

        <label className="flex items-center gap-2 text-xs text-fg-muted">
          <input
            type="checkbox"
            checked={wrap}
            onChange={(event) => setWrap(event.target.checked)}
            className="size-3.5 accent-accent"
          />
          {t("logs.wrap")}
        </label>

        {toolbarExtra}

        <div className="ml-auto flex items-center gap-4">
          {streaming && (
            <span className="flex items-center gap-2 text-xs text-fg-muted">
              <span className="dot-live" aria-hidden="true" />
              {t("logs.live")}
            </span>
          )}
          <span className="text-xs text-fg-subtle tabular-nums">
            {narrowed
              ? t("logs.linesOf", { shown: shown.length, total: all.length })
              : t("logs.lines", { count: all.length })}
          </span>
          {!empty && (
            <Button variant="ghost" size="sm" onClick={download}>
              {t("logs.download")}
              </Button>
          )}
        </div>
      </div>

      <div className="relative">
        <div
          ref={scrollRef}
          onScroll={(event) => {
            const element = event.currentTarget;
            // Scrolling up stops the follow so you can read; scrolling back to
            // the bottom starts it again, which is what a terminal does.
            const atBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 24;
            if (atBottom !== follow) setFollow(atBottom);
          }}
          className="max-h-[32rem] overflow-auto bg-canvas py-2"
        >
          {empty ? (
            <p className="px-4 py-3 font-mono text-xs text-fg-subtle">{emptyMessage}</p>
          ) : shown.length === 0 ? (
            <p className="px-4 py-3 font-mono text-xs text-fg-subtle">
              {t("logs.noMatch")}
            </p>
          ) : (
            <LogLines lines={shown} wrap={wrap} />
          )}
        </div>

        {/*
          Only while scrolled up. Following is the resting state, so a control
          to resume it is noise whenever it is already on.
        */}
        {!follow && !empty && (
          <button
            type="button"
            onClick={() => setFollow(true)}
            className="absolute inset-x-0 bottom-3 mx-auto w-fit border border-line-strong bg-raised px-3 py-1.5 text-xs text-fg shadow-sm transition-colors duration-200 ease-smooth hover:bg-surface"
          >
            {t("logs.jump")}
          </button>
        )}
      </div>
    </div>
  );
}
